import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Pencil } from "lucide-react";
import { Button } from "components/common/Button";
import { LayoutListRow } from "../LayoutListRow";
import { RoomInspector } from "../RoomInspector";
import { RoomsMergeBar } from "../RoomsMergeBar";
import { getLayoutShapeId } from "../utils/floorPlanLayout/getLayoutShapeId";
import { layoutHighlightStore } from "../utils/floorPlanLayout/layoutHighlightStore";
import { RoomsListHeader } from "./RoomsListHeader";
import type { RoomsListProps } from "./types";

const MIN_MERGED_ROOMS = 2;

const highlightOnHover = (shapeId: string) => (isHovered: boolean) =>
  layoutHighlightStore.set(isHovered ? shapeId : null);

export const RoomsList = ({
  roomElementCounts,
  roomColorById,
  canEdit,
  onOpenRoomsPanel,
  isLayoutDetectionRunning = false,
  onSelectRoom,
  onEditRoom,
  roomInspector = null,
  roomsMerge,
}: RoomsListProps) => {
  const { t } = useTranslation();

  const { selectedRoomIds, cancel: cancelMerge } = roomsMerge;

  const canMerge = canEdit && roomElementCounts.length >= MIN_MERGED_ROOMS;

  useEffect(() => () => layoutHighlightStore.set(null), []);

  // Leaving the list, by entering a room, a zone or another tab, drops the ticks.
  useEffect(() => cancelMerge, [cancelMerge]);

  return (
    <div className="flex flex-col gap-3 min-h-0">
      <RoomsListHeader
        roomCount={roomElementCounts.length}
        canEdit={canEdit}
        isMergeSelecting={selectedRoomIds !== null}
        isLayoutDetectionRunning={isLayoutDetectionRunning}
        onOpenRoomsPanel={onOpenRoomsPanel}
        onStartMerge={canMerge ? roomsMerge.start : undefined}
      />
      {selectedRoomIds !== null && (
        <RoomsMergeBar
          selectedCount={selectedRoomIds.length}
          name={roomsMerge.name}
          isMerging={roomsMerge.isMerging}
          onNameChange={roomsMerge.setName}
          onCancel={cancelMerge}
          onMerge={roomsMerge.merge}
        />
      )}
      <p className="text-xs text-gray-500">
        {selectedRoomIds !== null
          ? t("smartSurveyor.rooms.mergeHint")
          : t("smartSurveyor.rooms.listHint")}
      </p>
      <ul className="flex flex-col gap-2 overflow-y-auto min-h-0 p-1">
        {roomElementCounts.map(({ room, total }, position) => {
          if (roomInspector?.room.id === room.id) {
            return (
              <li key={room.id}>
                <RoomInspector state={roomInspector} />
              </li>
            );
          }
          const rowProps = {
            index: position + 1,
            name: room.name,
            colorRgb: roomColorById.get(room.id),
            detail: t("smartSurveyor.reviewPage.elementsCount", {
              count: total,
            }),
            onHoverChange: highlightOnHover(
              getLayoutShapeId({ kind: "room", id: room.id }),
            ),
          };
          if (selectedRoomIds !== null) {
            const isTicked = selectedRoomIds.includes(room.id);
            return (
              <LayoutListRow
                key={room.id}
                {...rowProps}
                onClick={() => roomsMerge.toggleRoom(room)}
                isSelected={isTicked}
                action={
                  <input
                    type="checkbox"
                    checked={isTicked}
                    onChange={() => roomsMerge.toggleRoom(room)}
                    aria-label={room.name}
                    className="mx-2 h-4 w-4 cursor-pointer accent-blue-600"
                  />
                }
              />
            );
          }
          // While an editor is open, any other row closes it instead of entering its room.
          const enterRoom = roomInspector
            ? roomInspector.cancel
            : () => onSelectRoom(room.id);
          return (
            <LayoutListRow
              key={room.id}
              {...rowProps}
              onClick={enterRoom}
              action={
                canEdit && onEditRoom ? (
                  <Button
                    variant="text"
                    size="sm"
                    aria-label={t("smartSurveyor.rooms.editRoom")}
                    title={t("smartSurveyor.rooms.editRoom")}
                    onClick={() => onEditRoom(room.id)}
                    className="!px-2 !text-gray-400 hover:!text-blue-600"
                  >
                    <Pencil size={16} />
                  </Button>
                ) : undefined
              }
            />
          );
        })}
      </ul>
    </div>
  );
};
